import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/src/modules/auth/useAuth';
import { BrandLogo } from '@/src/modules/components/BrandLogo';

export default function LoginPage() {
  const navigate = useNavigate();
  const { user, isAdmin, loading, error, loginWithGoogle } = useAuth();

  // Once Firebase reports an authenticated admin user, send them to the dashboard.
  useEffect(() => {
    if (!loading && user && isAdmin) {
      navigate('/dashboard', { replace: true });
    }
  }, [loading, user, isAdmin, navigate]);

  const handleGoogle = () => void loginWithGoogle();

  const accessDenied = !loading && user && !isAdmin;

  return (
    <div className="force-dark min-h-screen w-full flex flex-col md:flex-row bg-surface text-on-surface overflow-hidden relative">
      {/* Background Accents */}
      <div className="absolute top-[-10%] left-[-10%] w-[400px] h-[400px] bg-electric-violet/10 blur-[100px] rounded-full pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] bg-secondary-container/10 blur-[100px] rounded-full pointer-events-none" />

      {/* Left Section: Brand */}
      <section className="hidden md:flex flex-1 flex-col justify-between p-12 bg-surface-container-lowest border-r border-border-subtle relative overflow-hidden">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 space-y-8"
        >
          <BrandLogo variant="full" size="xl" href="/" />

          <div className="max-w-lg space-y-4">
            <h1 className="font-display text-5xl leading-tight text-on-surface font-bold">
              Admin Portal
            </h1>
            <p className="font-sans text-lg text-on-surface-variant leading-relaxed">
              Manage invitations, users, templates, and analytics from one unified dashboard.
            </p>
          </div>
        </motion.div>

        {/* Mockup Illustration */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="relative mt-12"
        >
          <div className="glass-panel rounded-xl p-6 shadow-2xl transform rotate-2 translate-x-12 translate-y-12 border-white/5">
            <div className="flex items-center gap-4 mb-6">
              <div className="h-12 w-12 rounded-full bg-surface-container-highest" />
              <div className="space-y-2">
                <div className="h-3 w-32 bg-surface-container-highest rounded" />
                <div className="h-2 w-24 bg-surface-container-low rounded" />
              </div>
            </div>
            <div className="space-y-4">
              <div className="h-32 w-full rounded-lg bg-surface-container-low/50 relative overflow-hidden backdrop-blur-md">
                <img 
                  src="https://pub-2b435ac1f79c40fbad08ae6c4e4f4143.r2.dev/public/assets/admin/abstract_ai.png"
                  className="w-full h-full object-cover opacity-40 mix-blend-overlay"
                  alt="Abstract AI"
                />
              </div>
              <div className="flex justify-between items-center">
                <div className="h-4 w-20 bg-primary/20 rounded" />
                <div className="h-8 w-24 bg-electric-violet rounded-lg shadow-lg shadow-electric-violet/20" />
              </div>
            </div>
          </div>
        </motion.div>

        <div className="relative z-10 font-display text-xs text-on-surface-variant flex gap-6">
          <span>© {new Date().getFullYear()} InviteCloud</span>
          <a href="https://invitecloud.in/company#privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Privacy Policy</a>
          <a href="https://invitecloud.in/company#terms" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Terms</a>
        </div>
      </section>

      {/* Right Section: Form */}
      <section className="flex-1 flex flex-col items-center justify-center p-6 relative">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          <div className="mb-8 flex justify-center md:hidden">
            <BrandLogo variant="full" size="lg" href="/" />
          </div>

          <div className="glass-panel p-8 md:p-10 rounded-2xl">
            <div className="mb-8">
              <h2 className="font-display text-3xl text-on-surface mb-2 font-bold">Welcome Back</h2>
              <p className="text-on-surface-variant font-sans">Sign in to your admin account.</p>
            </div>

            {accessDenied && (
              <div className="mb-6 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
                This Google account does not have admin access. Contact platform support.
              </div>
            )}
            {error && (
              <div className="mb-6 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
                {error}
              </div>
            )}
            <button
              type="button"
              onClick={handleGoogle}
              disabled={loading}
              className="w-full h-12 flex items-center justify-center gap-3 bg-surface-container-high hover:bg-surface-container-highest border border-border-subtle rounded-xl transition-all group mb-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"></path>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"></path>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"></path>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"></path>
              </svg>
              <span className="font-display text-sm font-medium">Continue with Google</span>
            </button>
          </div>
          
          <p className="mt-8 text-center text-on-surface-variant text-sm">
            Don't have an admin account? <a className="text-electric-violet font-semibold hover:underline" href="mailto:support@invitecloud.in">Contact Platform Support</a>
          </p>
        </motion.div>
      </section>
    </div>
  );
}
